import React from 'react';
import styled from 'styled-components';

import { shimmerFill } from '../../motion';
import { gaAlertAction } from '../lib/gaPixel';
import type { Alert } from '../hooks/useAlerts';

/**
 * The Hub's notification rows — the NO-ROLL-UP renderer.
 *
 * WHY THIS FILE EXISTS RATHER THAN A FLAG ON `AccountAlerts`. `AccountAlerts`
 * is mounted by `V4AccountDropdown`, the non-Hub `HeaderAccountMenu` and
 * `BoardAlertsBell`, all of which are `NO CHANGE` surfaces under THE-967's PRD
 * ("every page with the Hub switch off"). Its roll-up — `ROLLUP_THRESHOLD`
 * collapses 3+ `add_birthday` rows into one expandable row — is exactly what
 * the Hub removes. Threading a behaviour flag through a component with ten-odd
 * mount points is how one of those mount points silently changes; a separate
 * renderer cannot. (THE-967 plan, "Architecture call: where the new UI lives".)
 *
 * RENDERER ONLY. Which rows show, in what order, and how many there are is
 * decided by ONE row model, `hubAlertRows` / `hubAlertCount` in
 * `hub/hubAlerts.ts`, shared by the desktop avatar menu (`HubAccountMenu`), the
 * mobile You tab and its tab-bar dot. This file used to carry a second
 * selector + counter of its own (`visibleHubAlerts` / `hubAlertRowCount`); the
 * two disagreed on when the "Confirm your email" gate applies, so the desktop
 * number and the mobile list could differ for the same account. Never grow a
 * filter or a count here again — extend `hubAlerts.ts` instead.
 */

const Row = styled.div<{ $today?: boolean }>`
  display: grid;
  grid-template-columns: 24px 1fr 22px;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 150ms ease;
  ${({ $today }) =>
    $today
      ? `background: linear-gradient(120deg,
           hsl(var(--primary) / 0.26) 0%,
           hsl(var(--primary) / 0.10) 55%,
           transparent 100%);`
      : ''}
  &:hover {
    background: ${({ $today, theme }) =>
      $today
        ? `linear-gradient(120deg,
             hsl(var(--primary) / 0.34) 0%,
             hsl(var(--primary) / 0.14) 55%,
             transparent 100%)`
        : theme.color.creamLight};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 1px;
  }
  + [data-hub-alert-row] {
    margin-top: 2px;
  }
`;

const Emoji = styled.span`
  font-size: 18px;
  line-height: 1;
  text-align: center;
`;

const Headline = styled.span`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--foreground));
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Dismiss = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  border: none;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  &:hover {
    background: ${({ theme }) => theme.color.cream};
    color: hsl(var(--foreground));
  }
`;

const ShimmerRow = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px;
  + [data-hub-alert-shimmer] {
    margin-top: 2px;
  }
`;

const ShimmerDisc = styled.span`
  ${shimmerFill};
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  display: block;
`;

const ShimmerBar = styled.span<{ $width: string }>`
  ${shimmerFill};
  height: 12px;
  width: ${({ $width }) => $width};
  border-radius: 6px;
  display: block;
`;

export interface HubAlertListProps {
  /** The `alert` rows of `hubAlertRows`, already filtered and ordered. */
  alerts: readonly Alert[];
  onAlertClick: (alert: Alert) => void;
  onDismiss: (alertId: string) => void;
}

/**
 * One row per alert, no roll-up.
 *
 * KEYBOARD. The row is the focus stop (`data-hub-menu-item`, which is how the
 * host menu's arrow-key walker finds it — see `HubAccountMenu`); Enter/Space
 * activates it and Delete/Backspace dismisses it. The × keeps `tabIndex={-1}`
 * so arrowing down the menu steps row-to-row rather than alternating
 * row/dismiss/row, and `aria-keyshortcuts` is what tells a keyboard user the
 * Delete route exists instead of leaving dismissal mouse-only.
 */
export const HubAlertList: React.FC<HubAlertListProps> = ({
  alerts,
  onAlertClick,
  onDismiss,
}) => (
  <div data-testid="hub-alert-list">
    {alerts.map((a) => {
      const activate = () => {
        gaAlertAction({ action: 'click', alert_type: a.type, wording_shown: a.type === 'add_birthday' ? 'add_birthday' : 'standard' });
        onAlertClick(a);
      };
      const dismiss = () => {
        gaAlertAction({ action: 'dismiss', alert_type: a.type, wording_shown: a.type === 'add_birthday' ? 'add_birthday' : 'standard' });
        onDismiss(a.alertId);
      };
      return (
        <Row
          key={a.alertId}
          data-hub-alert-row
          data-hub-menu-item
          data-today={a.isToday || undefined}
          role="menuitem"
          tabIndex={0}
          aria-keyshortcuts="Delete"
          $today={a.isToday}
          onClick={activate}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activate();
              return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
              e.preventDefault();
              dismiss();
            }
          }}
        >
          <Emoji aria-hidden="true">{a.emoji || '🔔'}</Emoji>
          <Headline>{a.headline}</Headline>
          <Dismiss
            type="button"
            tabIndex={-1}
            aria-label={`Dismiss ${a.headline}`}
            onClick={(e) => {
              // Without this the row's own click handler also fires and
              // navigates the user away mid-dismiss.
              e.stopPropagation();
              dismiss();
            }}
          >
            ×
          </Dismiss>
        </Row>
      );
    })}
  </div>
);

export interface HubAlertShimmerProps {
  /** How many placeholder rows to draw. */
  rows?: number;
}

/**
 * Shape-matched placeholder rows for the notifications section.
 *
 * The PRD's hard rule is that "You're all caught up." must NEVER paint before
 * the data arrives — an empty state is an answer, and the surface does not have
 * one yet. So the loading branch renders these instead of nothing: a host that
 * rendered nothing while loading would be indistinguishable from a host with
 * zero rows, which is the same mistake in a different shape.
 */
export const HubAlertShimmer: React.FC<HubAlertShimmerProps> = ({ rows = 2 }) => (
  <div data-testid="hub-alert-shimmer" aria-hidden="true">
    {Array.from({ length: rows }, (_, i) => (
      <ShimmerRow key={i} data-hub-alert-shimmer>
        <ShimmerDisc />
        <ShimmerBar $width={i % 2 === 0 ? '78%' : '58%'} />
      </ShimmerRow>
    ))}
  </div>
);
